import {
  X,
  FileText,
  Calendar,
  Building2,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Clock,
  Package,
  Receipt,
  Truck,
  CreditCard,
  MessageSquare,
  Ban,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import type {
  Invoice, MatchStatus 
} from '../../types';
import {
  INVOICE_STATUS_LABELS, INVOICE_STATUS_COLORS, MATCH_STATUS_COLORS 
} from '../../types';

function MatchStatusBadge({ status }: { readonly status: MatchStatus }) {
  const colorClass = MATCH_STATUS_COLORS[status];
  
  const getIcon = () => {
    if (status === 'matched') return CheckCircle;
    if (status === 'mismatch') return AlertTriangle;
    return Clock;
  };
  const IconToRender = getIcon();
  
  return (
    <span className={`flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${colorClass}`}>
      <IconToRender className="h-3 w-3" />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

interface InvoiceDetailModalProps {
  readonly invoice: Invoice | null;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onApprove: (invoice: Invoice) => void;
  readonly onDispute: (invoice: Invoice) => void;
  readonly onMarkPaid: (invoice: Invoice) => void;
  readonly onViewPO: (invoice: Invoice) => void;
}

export function InvoiceDetailModal({
  invoice,
  isOpen,
  onClose,
  onApprove,
  onDispute,
  onMarkPaid,
  onViewPO,
}: InvoiceDetailModalProps) {
  if (!isOpen || !invoice) return null;

  const statusColor = INVOICE_STATUS_COLORS[invoice.status];
  const isOverdue = new Date(invoice.dueDate) < new Date() && invoice.status !== 'paid';
  const canApprove = invoice.status === 'pending' || invoice.status === 'matched';
  const canDispute = invoice.status !== 'paid' && invoice.status !== 'disputed';
  const canMarkPaid = invoice.status === 'approved';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-lg bg-background shadow-xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between border-b bg-background p-6">
          <div className="flex items-center gap-4">
            <div className="rounded-lg bg-primary/10 p-3">
              <FileText className="h-8 w-8 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold">{invoice.invoiceNumber}</h2>
                <span className={`rounded-full px-2 py-1 text-xs font-medium ${statusColor}`}>
                  {INVOICE_STATUS_LABELS[invoice.status]}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">{invoice.supplierName}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Key Dates & PO Reference */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border p-4">
              <h3 className="font-medium mb-3 flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                Invoice Details
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Invoice Date</span>
                  <span>{new Date(invoice.invoiceDate).toLocaleDateString()}</span>
                </div>
                <div className={`flex justify-between ${isOverdue ? 'text-red-600 font-medium' : ''}`}>
                  <span className={isOverdue ? '' : 'text-muted-foreground'}>Due Date</span>
                  <span className="flex items-center gap-1">
                    {new Date(invoice.dueDate).toLocaleDateString()}
                    {isOverdue && <AlertTriangle className="h-4 w-4" />}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created</span>
                  <span>{new Date(invoice.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            {invoice.poNumber && (
              <div className="rounded-lg border p-4">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  Purchase Order
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">PO Number</span>
                    <button
                      type="button"
                      onClick={() => onViewPO(invoice)}
                      className="text-primary hover:underline"
                    >
                      {invoice.poNumber}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Three-Way Match Status */}
          <div className="rounded-lg border p-4">
            <h3 className="font-medium mb-4">Three-Way Match Verification</h3>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="flex items-center gap-3 rounded-lg bg-blue-50 p-3">
                <Receipt className="h-8 w-8 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Purchase Order</p>
                  <p className="text-xs text-blue-600">{invoice.poNumber ?? 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-lg bg-green-50 p-3">
                <Truck className="h-8 w-8 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-green-900">Goods Receipt</p>
                  <p className="text-xs text-green-600">Verified</p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-lg bg-purple-50 p-3">
                <FileText className="h-8 w-8 text-purple-600" />
                <div>
                  <p className="text-sm font-medium text-purple-900">Invoice</p>
                  <p className="text-xs text-purple-600">{invoice.invoiceNumber}</p>
                </div>
              </div>
            </div>
            
            {invoice.matchStatus && (
              <div className="mt-4 flex items-center justify-between rounded-lg bg-muted/50 p-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Match Status:</span>
                  <MatchStatusBadge status={invoice.matchStatus} />
                </div>
                {invoice.matchVariance !== undefined && invoice.matchVariance > 0 && (
                  <span className="text-sm text-red-600 font-medium">
                    Variance: ${invoice.matchVariance.toFixed(2)}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Line Items */}
          <div>
            <h3 className="font-medium mb-3 flex items-center gap-2">
              <Package className="h-4 w-4 text-muted-foreground" />
              Line Items ({invoice.lines.length})
            </h3>
            <div className="rounded-lg border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Item</th>
                    <th className="px-4 py-2 text-right font-medium">Qty</th>
                    <th className="px-4 py-2 text-right font-medium">Unit Price</th>
                    <th className="px-4 py-2 text-right font-medium">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {invoice.lines.map((line) => (
                    <tr key={line.lineId} className="hover:bg-muted/30">
                      <td className="px-4 py-3">{line.itemName}</td>
                      <td className="px-4 py-3 text-right text-muted-foreground">
                        {line.quantity} {line.unit}
                      </td>
                      <td className="px-4 py-3 text-right text-muted-foreground">
                        ${line.unitPrice.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        ${line.totalPrice.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="rounded-lg border p-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span>${invoice.subtotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tax</span>
                <span>${invoice.tax.toLocaleString()}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="font-medium">Total</span>
                <div className="flex items-center gap-1 text-lg font-bold text-primary">
                  <DollarSign className="h-5 w-5" />
                  {invoice.total.toLocaleString()}
                  <span className="text-xs text-muted-foreground font-normal ml-1">
                    {invoice.currency}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="rounded-lg border p-4">
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
                Notes
              </h3>
              <p className="text-sm text-muted-foreground">{invoice.notes}</p>
            </div>
          )}
        </div>

        {/* Actions Footer */}
        <div className="sticky bottom-0 border-t bg-background p-4">
          <div className="flex flex-wrap gap-2 justify-end">
            {canDispute && (
              <Button variant="outline" onClick={() => onDispute(invoice)}>
                <Ban className="mr-2 h-4 w-4" />
                Dispute
              </Button>
            )}
            {invoice.poNumber && (
              <Button variant="outline" onClick={() => onViewPO(invoice)}>
                <Receipt className="mr-2 h-4 w-4" />
                View PO
              </Button>
            )}
            {canMarkPaid && (
              <Button variant="outline" onClick={() => onMarkPaid(invoice)}>
                <CreditCard className="mr-2 h-4 w-4" />
                Mark as Paid
              </Button>
            )}
            {canApprove && (
              <Button onClick={() => onApprove(invoice)}>
                <CheckCircle className="mr-2 h-4 w-4" />
                Approve Invoice
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
